import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Checkbox,
  Stack,
  Backdrop,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";

// Styles
import "../OpdStepper.css";



export const ESSENTIAL_PACKAGES = [
  { packageCode: "EPS01", displayName: "Care in Pregnancy & Child Birth", image: "eps-1.svg" },
  { packageCode: "EPS02", displayName: "Neonatal & Infant Healthcare Services", image: "eps-2.svg" },
  { packageCode: "EPS03", displayName: "Childhood & Adolescent Healthcare Services", image: "eps-3.svg" },
  { packageCode: "EPS04", displayName: "Reproductive & Family Planning Services", image: "eps-4.svg" },
  { packageCode: "EPS05", displayName: "Management of Communicable Diseases", image: "eps-5.svg" },
  { packageCode: "EPS06", displayName: "Outpatient Care for Acute Simple Illness", image: "eps-6.svg" },
  { packageCode: "EPS07", displayName: "Screening Prevention & Control of NCDs", image: "eps-7.svg" },
  { packageCode: "EPS08", displayName: "Oral Care", image: "eps-8.svg" },
  { packageCode: "EPS09", displayName: "Eye & ENT Care", image: "eps-9.svg" },
  { packageCode: "EPS10", displayName: "Elderly & Palliative Care", image: "eps-10.svg" },
  { packageCode: "EPS11", displayName: "Emergency Care", image: "eps-11.svg" },
  { packageCode: "EPS12", displayName: "Mental Health Care", image: "eps-12.svg" },
];

export const diagnosisValidationSchema = Yup.object().shape({
  provisionalDiagnosis: Yup.string().trim().max(1000, "Diagnosis cannot exceed 1000 characters"),
  counselling: Yup.array().of(Yup.number()),
});

export default function DiagnosisCounselling({
  nextStep,
  prevStep,
  initialValues,
  submitLabel = "Next",
  showActions = true,
}) {
  const [expanded, setExpanded] = useState({
    counselling: true,
    packages: true,
  });

  const [loadingCounselling, setLoadingCounselling] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [counsellingList, setCounsellingList] = useState([]);
  const [savedPackages, setSavedPackages] = useState([]);
  const [step6Data, setStep6Data] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // Fetch counselling options, saved packages, and saved step 6 data on mount
  useEffect(() => {
    const loadCounselling = async () => {
      setLoadingCounselling(true);
      try {
        const res = await eopdAPI.getCounsellingOptions();
        if (res && res.success && Array.isArray(res.counselling)) {
          setCounsellingList(res.counselling);
        }
      } catch (err) {
        console.error("Error fetching counselling options:", err);
      } finally {
        setLoadingCounselling(false);
      }
    };

    const loadPackagesAndStep6 = async () => {
      setLoadingPackages(true);
      try {
        const displayId = localStorage.getItem("currentOpdDisplayId") || "";
        if (displayId) {
          const [pkgRes, stepRes] = await Promise.all([
            eopdAPI.getSavedPackages(displayId).catch(() => null),
            eopdAPI.getOpdStepData(displayId, 6).catch(() => null),
          ]);
          if (pkgRes && pkgRes.success && Array.isArray(pkgRes.saved)) {
            const codes = pkgRes.saved.map((pkg) => pkg.package_code);
            setSavedPackages(codes);
          }
          if (stepRes && stepRes.success && stepRes.data) {
            setStep6Data(stepRes.data);
          }
        }
      } catch (err) {
        console.error("Error fetching saved packages or step 6 data:", err);
      } finally {
        setLoadingPackages(false);
      }
    };

    loadCounselling();
    loadPackagesAndStep6();
  }, []);

  const initialFormValues = useMemo(() => {
    const diag = step6Data?.provisional_diagnosis || initialValues?.provisionalDiagnosis || "";
    const couns = Array.isArray(step6Data?.counselling)
      ? step6Data.counselling.map((c) => (typeof c === "object" ? (c.counselling_id ?? c.con_id ?? c.id) : c))
      : initialValues?.counselling || [];
    const pkgs = Array.isArray(step6Data?.packages) && step6Data.packages.length > 0
      ? step6Data.packages.map((p) => (typeof p === "object" ? p.package_code : p))
      : savedPackages;

    return {
      provisionalDiagnosis: diag,
      counselling: couns,
      packages: pkgs,
    };
  }, [initialValues, savedPackages, step6Data]);

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={diagnosisValidationSchema}
      enableReinitialize
      onSubmit={async (values, helpers) => {
        try {
          const displayId = localStorage.getItem("currentOpdDisplayId") || "";
          const payload = {
            display_id: displayId,
            p_diagnosis: values.provisionalDiagnosis?.trim() || "",
            counselling: values.counselling.map(Number),
            packages: values.packages || [],
          };
          const response = await eopdAPI.saveOpdStep(6, payload);
          if (response && response.success) {
            toast.success(response.message || "Diagnosis & Counselling details saved successfully!");
            nextStep();
          } else {
            toast.error(response.message || "Failed to save details.");
          }
        } catch (error) {
          console.error(error);
          const errMsg = error.response?.data?.message || error.message || "Failed to save details.";
          toast.error(errMsg);
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
        const toggleCounselling = (id) => {
          const list = values.counselling || [];
          const next = list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
          setFieldValue("counselling", next);
        };

        const togglePackage = (code) => {
          const list = values.packages || [];
          const next = list.includes(code) ? list.filter((item) => item !== code) : [...list, code];
          setFieldValue("packages", next);
        };

        return (
          <Form noValidate>
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isSubmitting}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            <Typography className="opd-step-title">
              Diagnosis &amp; Counselling
            </Typography>

            {/* Card 1: Provisional Diagnosis */}
            <Box className="opd-diagnosis-card">
              <Typography className="opd-diagnosis-card-title">
                Provisional Diagnosis
              </Typography>
              <Typography className="opd-diagnosis-card-subtitle">
                Diagnosis
              </Typography>

              <Box className="opd-input-group">
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  name="provisionalDiagnosis"
                  value={values.provisionalDiagnosis}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Working diagnosis based on findings"
                  error={Boolean(touched.provisionalDiagnosis && errors.provisionalDiagnosis)}
                  helperText={touched.provisionalDiagnosis && errors.provisionalDiagnosis ? errors.provisionalDiagnosis : ""}
                  className="opd-input-field"
                />
              </Box>
            </Box>

            {/* Card 2: Counselling Provided (Accordion) */}
            <Accordion
              expanded={expanded.counselling}
              onChange={handleAccordionChange("counselling")}
              className={`opd-accordion ${expanded.counselling ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">Counselling Provided</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                {loadingCounselling ? (
                  <Stack direction="row" justifyContent="center" sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                  </Stack>
                ) : (
                  <Grid container spacing={2}>
                    {counsellingList.map((opt) => {
                      const isChecked = values.counselling.includes(opt.id);
                      return (
                        <Grid size={{ xs: 12, sm: 6 }} key={opt.id}>
                          <Box
                            className={`opd-counselling-item ${isChecked ? "opd-counselling-item-checked" : ""}`}
                            onClick={() => toggleCounselling(opt.id)}
                          >
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              readOnly
                              sx={{ p: 0 }}
                            />
                            <Typography
                              className={`opd-counselling-text ${isChecked ? "opd-counselling-text-checked" : ""}`}
                            >
                              {opt.con_name}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Card 3: Essential Package of Services and Counselling Provided (Accordion) */}
            <Accordion
              expanded={expanded.packages}
              onChange={handleAccordionChange("packages")}
              className={`opd-accordion ${expanded.packages ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">Essential Package of Services and Counselling Provided</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                {loadingPackages ? (
                  <Stack direction="row" justifyContent="center" sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                  </Stack>
                ) : (
                  <Grid container spacing={2}>
                    {ESSENTIAL_PACKAGES.map((pkg) => {
                      const isHighlighted = (values.packages || []).includes(pkg.packageCode);

                      return (
                        <Grid size={{ xs: 6, sm: 4, md: 2 }} key={pkg.packageCode}>
                          <Box
                            className={`opd-package-item ${isHighlighted ? "opd-package-item-highlighted" : ""}`}
                            onClick={() => togglePackage(pkg.packageCode)}
                            sx={{ cursor: "pointer" }}
                          >
                            <Box
                              component="img"
                              src={`/images/essential_package/${pkg.image}`}
                              alt={pkg.displayName}
                              className={`opd-package-img ${isHighlighted ? "opd-package-img-highlighted" : ""}`}
                            />
                            <Typography
                              className={`opd-package-text ${isHighlighted ? "opd-package-text-highlighted" : ""}`}
                            >
                              {pkg.displayName}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>

            {showActions && (
              <Stack
                direction="row"
                justifyContent={prevStep ? "space-between" : "flex-end"}
                alignItems="center"
                sx={{ mt: 3 }}
              >
                {prevStep && (
                  <Button
                    type="button"
                    variant="outlined"
                    className="opd-back-btn"
                    onClick={prevStep}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting}
                  className="opd-next-btn"
                >
                  {submitLabel}
                </Button>
              </Stack>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
